import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { getSecteur } from '../../utils/retail';
import { RefreshCw, Trash2, Zap } from 'lucide-react';
import { toast } from '../ui/sonner';

/** 02/07 — Consignes de réapprovisionnement automatique par famille. */
export const ReaproAutoPanel = () => {
  const retail = useGameStore(s => s.retail);
  const activeInvestments = useGameStore(s => s.activeInvestments) || {};
  const setConsigneReapro = useGameStore(s => s.setConsigneReapro);
  const supprimerConsigneReapro = useGameStore(s => s.supprimerConsigneReapro);
  const pilotageCentrale = useGameStore(s => s.pilotageCentrale);

  const [famId, setFamId] = useState('');
  const [seuil, setSeuil] = useState('');
  const [qty, setQty] = useState('');
  const [prixMax, setPrixMax] = useState('');

  const sec = getSecteur(retail.secteur);
  const consignes = retail.reapro || {};
  const centraleActive = !!activeInvestments['grp-centrale'];

  // 02/07 — suggestion : seuil = 10 j de ventes réelles, quantité = 30 j.
  const suggestionPour = (id) => {
    const hist = retail.ventes7?.[id] || [];
    const vj = hist.length ? hist.reduce((a, b) => a + b, 0) / hist.length : 0;
    return { seuil: Math.max(5, Math.round(vj * 10)), qty: Math.max(10, Math.round(vj * 30)), vj };
  };
  const [suggestion, setSuggestion] = useState(null);
  const choisirFamille = (id) => {
    setFamId(id);
    if (!id) { setSuggestion(null); return; }
    const existante = consignes[id];
    if (existante) {
      setSeuil(String(existante.seuil));
      setQty(String(existante.qty));
      setPrixMax(existante.prixMax != null ? String(existante.prixMax) : '');
      setSuggestion(null);
    } else {
      const s = suggestionPour(id);
      setSeuil(String(s.seuil));
      setQty(String(s.qty));
      setSuggestion(s);
    }
  };

  const enregistrer = () => {
    const res = setConsigneReapro({
      famId,
      seuil: parseInt(seuil, 10),
      qty: parseInt(qty, 10),
      prixMax: prixMax === '' ? null : parseFloat(prixMax),
    });
    if (res?.ok) {
      const fam = sec.familles.find(f => f.id === famId);
      toast('Consigne enregistrée', { description: `${fam?.nom} : réapro auto de ${qty} u. dès que le stock passe sous ${seuil} u.${prixMax ? ` (prix max ${prixMax} €/u.)` : ''}` }); // i18n-keep
      setFamId(''); setSeuil(''); setQty(''); setPrixMax('');
    } else {
      toast.error('Consigne invalide', { description: 'Renseignez une famille, un seuil (≥ 0) et une quantité (> 0).' }); // i18n-keep
    }
  };

  return (
    <div className="surface-card p-4" data-testid="reapro-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-accent" />
          <h3 className="font-bold text-sm text-foreground">Réapprovisionnement automatique{/* i18n-keep */}</h3>
        </div>
        {centraleActive && (
          <button
            type="button"
            onClick={() => {
              const res = pilotageCentrale();
              if (res?.ok) toast('Pilotage centrale activé ⚡', { description: `${res.nb} consignes configurées automatiquement (seuil = 10 j de ventes, quantité = 30 j).` }); // i18n-keep
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs font-bold hover:bg-amber-500/25"
            data-testid="pilotage-centrale-btn"
          >
            <Zap className="w-3.5 h-3.5" /> Pilotage centrale{/* i18n-keep */}
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Donnez une consigne par famille : dès que le stock passe sous le seuil, une commande part automatiquement chez le fournisseur le moins cher qui couvre la gamme (dans la limite de votre prix max).{/* i18n-keep */}
      </p>

      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
        <label className="text-[10px] uppercase font-bold text-slate-500">
          Famille{/* i18n-keep */}
          <select value={famId} onChange={e => choisirFamille(e.target.value)} className="mt-1 w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200" data-testid="reapro-famille">
            <option value="">— Choisir —</option>
            {sec.familles.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
          </select>
        </label>
        <label className="text-[10px] uppercase font-bold text-slate-500">
          Stock mini (u.){/* i18n-keep */}
          <input type="number" min="0" value={seuil} onChange={e => setSeuil(e.target.value)} className="mt-1 w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200" data-testid="reapro-seuil" />
        </label>
        <label className="text-[10px] uppercase font-bold text-slate-500">
          Qté à commander{/* i18n-keep */}
          <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} className="mt-1 w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200" data-testid="reapro-qty" />
        </label>
        <label className="text-[10px] uppercase font-bold text-slate-500">
          Prix max €/u. (opt.){/* i18n-keep */}
          <input type="number" min="0" step="0.1" value={prixMax} onChange={e => setPrixMax(e.target.value)} className="mt-1 w-full bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-xs text-slate-200" data-testid="reapro-prixmax" />
        </label>
        <button
          type="button"
          onClick={enregistrer}
          disabled={!famId || seuil === '' || qty === ''}
          className="px-3 py-2 rounded-md bg-accent text-ink-900 text-xs font-black disabled:opacity-40"
          data-testid="reapro-enregistrer"
        >Enregistrer{/* i18n-keep */}</button>
      </div>
      {suggestion && famId && (
        <p className="text-[10px] text-slate-500 mt-1.5" data-testid="reapro-suggestion">
          💡 Suggestion pré-remplie d'après vos ventes réelles ({suggestion.vj.toFixed(1)} u./jour en moyenne sur 7 jours) : seuil = 10 jours, quantité = 30 jours. Ajustez librement.{/* i18n-keep */}
        </p>
      )}

      {Object.keys(consignes).length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="reapro-consignes">
          {Object.entries(consignes).map(([id, c]) => {
            const fam = sec.familles.find(f => f.id === id);
            if (!fam || !c) return null;
            const stQty = retail.stock?.[id]?.qty || 0;
            return (
              <div key={id} className={`flex items-center justify-between gap-2 rounded border px-3 py-1.5 text-xs ${c.alerted ? 'border-rose-500/40 bg-rose-500/5' : 'border-ink-600 bg-ink-900/40'}`} data-testid={`reapro-consigne-${id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-200">{fam.nom}</span>
                  {c.centrale && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">Centrale{/* i18n-keep */}</span>}
                  <span className="font-mono text-slate-400">
                    seuil {c.seuil} u. → commande {c.qty} u.{c.prixMax != null ? ` · prix max ${c.prixMax.toFixed(2)} €` : ''}{/* i18n-keep */}
                  </span>
                  <span className={`font-mono ${stQty <= c.seuil ? 'text-amber-300' : 'text-slate-500'}`}>stock : {stQty} u.{/* i18n-keep */}</span>
                  {c.alerted && <span className="text-[10px] font-bold text-rose-300">⚠ bloqué (voir messagerie){/* i18n-keep */}</span>}
                </div>
                <button type="button" onClick={() => supprimerConsigneReapro(id)} className="text-slate-500 hover:text-rose-400" data-testid={`reapro-supprimer-${id}`} aria-label="Supprimer la consigne">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReaproAutoPanel;
